import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import InsightsIcon from "@mui/icons-material/Insights";
import { getFormulaScorerPreview } from "../../services";
import {
  FormulaDecision,
  FormulaScorerPreviewResponse,
} from "../../types/formula-scorer.type";
import { fs } from "./tokens";
import { deriveRow, summarize } from "./derive";
import SummaryCards from "./SummaryCards";
import CustomerTable from "./CustomerTable";
import FormulaChip from "./FormulaChip";

const DEFAULT_WEEKS = 8;

export default function FormulaScorer() {
  const [routeCode, setRouteCode] = useState("");
  const [weeks, setWeeks] = useState(DEFAULT_WEEKS);
  const [data, setData] = useState<FormulaScorerPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadedRoute, setLoadedRoute] = useState<string | null>(null);

  const load = useCallback(async () => {
    const route = routeCode.trim();
    if (!route) return;
    setLoading(true);
    try {
      const {
        data: { data: payload },
      } = await getFormulaScorerPreview({ route_code: route, weeks });
      setData(payload);
      setLoadedRoute(route);
    } finally {
      setLoading(false);
    }
  }, [routeCode, weeks]);

  // Confirm / reject only change ONE customer's decision, and to exactly the value the
  // backend would report on a re-fetch. Patch that row in place: re-running the preview
  // re-scores every customer on the route, which is by far the most expensive call on
  // this screen and would make every click cost seconds.
  const applyDecision = useCallback(
    (customerCode: string, decision: FormulaDecision) => {
      setData((prev) =>
        prev
          ? {
              ...prev,
              results: prev.results.map((r) =>
                r.customer_code === customerCode ? { ...r, decision } : r,
              ),
            }
          : prev,
      );
    },
    [],
  );

  const rows = useMemo(
    () => (data ? data.results.map(deriveRow) : []),
    [data],
  );
  const summary = useMemo(() => summarize(rows), [rows]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, color: fs.text }}>
      {/* Title */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2.5,
            bgcolor: fs.charcoal,
            color: fs.amber,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <InsightsIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: fs.text }}>
            Formula Scorer · คะแนนสูตรลงของ
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: fs.dim, mt: 0.25, lineHeight: 1.5 }}>
            ระบบคำนวณย้อนหลังว่าลูกค้าแต่ละรายควรใช้ "สูตรลงของ" ไหน แล้วเสนอให้คนพิจารณายืนยัน —
            ระบบเป็นเพียงผู้แนะนำ การสลับสูตรจริงต้องกดยืนยันเอง
          </Typography>
        </Box>
        <Box
          sx={{
            fontSize: 11,
            fontWeight: 600,
            color: fs.amber,
            bgcolor: fs.amberSoft,
            border: `1px solid ${fs.amberLine}`,
            px: 1,
            py: 0.5,
            borderRadius: 1.5,
            whiteSpace: "nowrap",
          }}
        >
          โหมดแนะนำ · มนุษย์ตัดสินใจขั้นสุดท้าย
        </Box>
      </Box>

      {/* Filter */}
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
        <TextField
          size="small"
          label="รหัสสายส่ง (route)"
          value={routeCode}
          onChange={(e) => setRouteCode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          sx={{ minWidth: 200 }}
        />
        <TextField
          size="small"
          type="number"
          label="ช่วงย้อนหลัง (สัปดาห์)"
          value={weeks}
          onChange={(e) => setWeeks(Math.max(1, Number(e.target.value) || DEFAULT_WEEKS))}
          sx={{ width: 160 }}
        />
        <Button
          variant="contained"
          disabled={loading || !routeCode.trim()}
          onClick={load}
          sx={{ bgcolor: fs.charcoal, "&:hover": { bgcolor: "#2b2e36" } }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "โหลด"}
        </Button>
      </Box>

      {!data && !loading && (
        <Box
          sx={{
            border: `1px dashed ${fs.line}`,
            borderRadius: 3,
            p: 5,
            textAlign: "center",
            color: fs.faint,
          }}
        >
          ใส่รหัสสายส่งแล้วกด "โหลด" เพื่อดูคะแนนสูตรของลูกค้าในสาย
        </Box>
      )}

      {data && (
        <>
          {/* Run meta strip */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexWrap: "wrap",
              fontSize: 12,
              color: fs.dim,
              bgcolor: fs.surface2,
              border: `1px solid ${fs.line}`,
              borderRadius: 2,
              px: 2,
              py: 1,
            }}
          >
            <span>
              สายส่ง: <b style={{ color: fs.text }}>{loadedRoute}</b>
            </span>
            <span>
              ช่วง:{" "}
              <b style={{ color: fs.text }}>
                {data.window_start} → {data.window_end}
              </b>
            </span>
            <span>
              ลูกค้าในสาย: <b style={{ color: fs.text }}>{data.customer_count}</b>
            </span>
            <span>
              ประเมินได้: <b style={{ color: fs.text }}>{data.results.length}</b>
            </span>
          </Box>

          <SummaryCards summary={summary} />

          {/* Legend */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              fontSize: 12,
              color: fs.dim,
            }}
          >
            <span>สูตรลงของ:</span>
            <FormulaChip code="SAME_DAY" size={12} />
            <FormulaChip code="NEXT_DAY" size={12} />
            <Typography sx={{ fontSize: 11, color: fs.faint }}>
              — สีใช้เพื่อ <b>แยกสูตร</b> เท่านั้น ไม่ได้บอกว่าสูตรไหนดีกว่า
            </Typography>
          </Box>

          {data.results.length === 0 ? (
            <Box
              sx={{
                border: `1px dashed ${fs.line}`,
                borderRadius: 3,
                p: 5,
                textAlign: "center",
                color: fs.faint,
              }}
            >
              ไม่มีลูกค้าที่มีรอบพอจะประเมินในสายนี้
            </Box>
          ) : (
            <CustomerTable rows={rows} onDecided={applyDecision} onReload={load} />
          )}

          <Divider sx={{ borderColor: fs.line }} />
          <Typography sx={{ fontSize: 11, color: fs.faint, lineHeight: 1.6 }}>
            ตัวเลขทั้งหมดเป็นค่าประเมินต่อ 1 รอบลงของ · "ของเสียลดลง" และ "โอกาสขายที่ได้คืน"
            คำนวณจากต้นทุน/กำไรจริงของสินค้าที่มีข้อมูลครบ · การสลับสูตรมีผลรอบถัดไปหลังยืนยัน
          </Typography>
        </>
      )}
    </Box>
  );
}
